import { fromPolyBool, polygonToCompoundPath, toPolyBool } from '@/modules/Editor/compoundPath'
import type { Action } from '@/modules/Editor/managers/actionManager'
import { combine, polygon, segments, selectUnion } from '@/modules/Editor/polygonOperations'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import { shallowCloneAnnotation } from '@/modules/Editor/models/annotation/cloneAnnotation'
import type { View } from '@/modules/Editor/views/view'
import { setContext } from '@/services/sentry'

import { mergeSubAnnotations } from './utils/mergeSubAnnotations'
import { isPolygon } from '@/modules/Editor/annotationTypes/polygon'
import type { Polygon } from '@/modules/Editor/AnnotationData'
import { ToolEvents } from '@/modules/Editor/eventBus'
import type { ToolEvent } from '@/modules/Editor/tools/types'
import { ToolName } from '@/modules/Editor/tools/types'

const editToolEvent: ToolEvent = {
  toolName: ToolName.Edit,
  lastHotkeyPressed: null,
  editing: false,
}

export const mergePolygons = (polygonA: Polygon, polygonB: Polygon): Polygon | null => {
  if (!isPolygon(polygonA) || !isPolygon(polygonB)) {
    return null
  }

  const polygonCompoundPath1 = polygonToCompoundPath(polygonA)
  const polygonCompoundPath2 = polygonToCompoundPath(polygonB)
  const segment1 = selectUnion(
    combine(segments(toPolyBool(polygonCompoundPath1)), { segments: [], inverted: false }),
  )
  const segment2 = selectUnion(
    combine(segments(toPolyBool(polygonCompoundPath2)), { segments: [], inverted: false }),
  )

  const combined = combine(segment1, segment2)

  const mergedSegment = selectUnion(combined)
  const mergedPolygon = fromPolyBool(polygon(mergedSegment))

  return {
    path: mergedPolygon.path,
    additionalPaths: mergedPolygon.additionalPaths,
  }
}

export const mergePolygonsAction = (
  view: View,
  firstAnnotation: Annotation,
  secondAnnotation: Annotation,
): Action => {
  const previousFirstAnnotationData = firstAnnotation.data
  const { id: secondAnnotationId } = secondAnnotation

  return {
    do(): boolean {
      if (!isPolygon(firstAnnotation.data) || !isPolygon(secondAnnotation.data)) {
        return false
      }

      const mergedPolygon = mergePolygons(firstAnnotation.data, secondAnnotation.data)
      if (!mergedPolygon) {
        return false
      }

      const subAnnotations = mergeSubAnnotations(firstAnnotation, secondAnnotation)

      const updatedAnnotation = shallowCloneAnnotation(firstAnnotation, {
        data: {
          ...firstAnnotation.data,
          path: mergedPolygon.path,
          additionalPaths: mergedPolygon.additionalPaths,
        },
        subAnnotations,
      })

      ToolEvents.polygonUpdating.emit(editToolEvent, [updatedAnnotation.id, secondAnnotationId])
      view.annotationManager.updateAnnotation(updatedAnnotation)
      view.annotationManager.deleteAnnotation(secondAnnotationId)

      return true
    },
    undo(): boolean {
      firstAnnotation.data = previousFirstAnnotationData
      ToolEvents.polygonUpdating.emit(editToolEvent, [secondAnnotation.id, firstAnnotation.id])
      try {
        view.annotationManager.createAnnotation(secondAnnotation)
        view.annotationManager.selectAnnotations([secondAnnotation.id])
      } catch (e) {
        setContext('error', { error: e })
        console.error('in v2 mergePolygons action, annotatonManager failed to create')
        return false
      }
      view.annotationManager.updateAnnotation(firstAnnotation)

      return true
    },
  }
}
